<script setup lang="ts">
import type { EverContactInfo } from "@/types";

const props = defineProps({
  modelValue: {
    type: Object as PropType<EverContactInfo>,
    required: true,
  },
});

const countries = ref([]);

onBeforeMount(async () => {
  countries.value = await $fetch("/api/countries");
});

const emit = defineEmits(["update:modelValue"]);

const formModel = ref({ ...props.modelValue });

const { validTextInput, validEmail } = useFormUtils();
const validationError = ref(false);

const validate = (): boolean => {
  if (
    !validTextInput.value(formModel.value.firstname) ||
    !validTextInput.value(formModel.value.lastname) ||
    !validEmail.value(formModel.value.email) ||
    !validTextInput.value(formModel.value.country) ||
    !validTextInput.value(formModel.value.firstPartnerName) ||
    !validTextInput.value(formModel.value.secondPartnerName)
  ) {
    validationError.value = true;
    return false;
  }

  emit("update:modelValue", formModel.value);
  return true;
};

defineExpose({ validate });
</script>

<template>
  <h1>Reserve your Celebration</h1>
  <form class="contact-form">
    <h2>Contact <span class="sub">(* required fields)</span></h2>
    <div class="form-row">
      <div class="form-item">
        <FormsInputField
          :value="formModel.firstname"
          name="firstname"
          type="text"
          placeholder="First Name *"
          required
          :error="!validTextInput(formModel.firstname) && validationError"
          error-message="First name is required"
          @input="(evt) => (formModel.firstname = evt)"
        />
      </div>

      <div class="form-item">
        <FormsInputField
          :value="formModel.lastname"
          name="lastname"
          type="text"
          placeholder="Last Name *"
          required
          :error="!validTextInput(formModel.lastname) && validationError"
          error-message="Last name is required"
          @input="(evt) => (formModel.lastname = evt)"
        />
      </div>
    </div>

    <div class="form-item">
      <FormsInputField
        :value="formModel.email"
        name="email"
        type="email"
        placeholder="Email *"
        required
        :error="!validEmail(formModel.email) && validationError"
        error-message="Email is required"
        @input="(evt) => (formModel.email = evt)"
      />
    </div>

    <div class="form-row">
      <div class="form-item custom-select">
        <FormsSelectField
          name="country"
          :value="formModel.country"
          :options="countries"
          :error="!validTextInput(formModel.country) && validationError"
          error-message="Select country of Residence"
          @select="(evt) => (formModel.country = evt)"
        />
      </div>

      <div class="form-item">
        <FormsPhoneInputField
          :value="formModel.phone"
          name="phone"
          placeholder="Phone Number"
          @input="($evt) => (formModel.phone = $evt)"
        />
      </div>
    </div>

    <h2>About the Couple</h2>

    <div class="form-row">
      <div class="form-item custom-select">
        <FormsInputField
          :value="formModel.firstPartnerName"
          name="firstPartnerName"
          type="text"
          placeholder="Full Name of First Partner *"
          required
          :error="!validTextInput(formModel.firstPartnerName) && validationError"
          error-message="First partner name is required"
          @input="($evt) => (formModel.firstPartnerName = $evt)"
        />
      </div>

      <div class="form-item">
        <FormsInputField
          :value="formModel.secondPartnerName"
          name="secondPartnerName"
          type="text"
          placeholder="Full Name of Second Partner *"
          required
          :error="!validTextInput(formModel.secondPartnerName) && validationError"
          error-message="Second partner name is required"
          @input="($evt) => (formModel.secondPartnerName = $evt)"
        />
      </div>
    </div>
  </form>
</template>

<style lang="scss">
@use "@/assets/scss/forms";

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 3rem;

  h2 {
    align-items: center;
    display: flex;
    gap: 1rem;
    .sub {
      font-size: 1.25rem;
      font-weight: 200;
    }
  }
}

select {
  @include forms.form-select;
}
</style>
